import type { LocationTreeNode } from '../api/schedulingApis';

/**
 * Collect location_id of the given node and all its descendants into a Set.
 */
function collectLocationIdAndDescendants(nodes: LocationTreeNode[], targetLocationId: string): Set<string> {
  const set = new Set<string>();
  const targetStr = String(targetLocationId);

  function collect(nodesList: LocationTreeNode[]) {
    for (const n of nodesList) {
      if (n.location_id) set.add(String(n.location_id));
      if (n.child?.length) collect(n.child);
    }
  }

  function findAndCollect(nodesList: LocationTreeNode[]): boolean {
    for (const n of nodesList) {
      if (String(n.location_id) === targetStr) {
        set.add(String(n.location_id));
        if (n.child?.length) collect(n.child);
        return true;
      }
      if (n.child?.length && findAndCollect(n.child)) return true;
    }
    return false;
  }

  findAndCollect(nodes);
  return set;
}

export interface TaskLike {
  parentTaskId: string | null;
  taskType?: string | null;
  locationId?: string | null;
}

/**
 * Compute the set of location IDs that are valid for selection when the location
 * panel is opened for the given task.
 * - Walk up the task's parent chain; find the nearest ancestor with taskType "Location".
 * - If found and it has a locationId, return that location + its descendants (only those selectable).
 * - If none found, return null (all locations are enabled).
 */
export function getEnabledLocationIds(
  getTask: (id: string) => TaskLike | undefined,
  taskId: string,
  locationTree: LocationTreeNode[] | null
): Set<string> | null {
  if (!taskId || !locationTree?.length) return null;

  const task = getTask(taskId);
  if (!task) return null;

  let current: TaskLike | undefined = task;
  while (current?.parentTaskId) {
    const parent = getTask(current.parentTaskId);
    if (!parent) break;
    const parentType = String(parent.taskType ?? '').toLowerCase();
    if (parentType === 'location') {
      const locId = parent.locationId ?? null;
      if (locId != null && String(locId).trim() !== '') {
        const set = collectLocationIdAndDescendants(locationTree, String(locId));
        return set.size > 0 ? set : null;
      }
    }
    current = parent;
  }

  return null;
}

/**
 * Get the location ID to use for display (Location name column).
 * Returns the task's own locationId if set; otherwise walks up to the nearest
 * ancestor with taskType "Location" and returns that ancestor's locationId.
 */
export function getEffectiveLocationIdForDisplay(
  getTask: (id: string) => TaskLike | undefined,
  taskId: string
): string | null {
  const task = getTask(taskId);
  if (!task) return null;
  if (task.locationId != null && String(task.locationId).trim() !== '') {
    return String(task.locationId);
  }
  let current: TaskLike | undefined = task;
  while (current?.parentTaskId) {
    const parent = getTask(current.parentTaskId);
    if (!parent) break;
    const parentType = String(parent.taskType ?? '').toLowerCase();
    if (parentType === 'location') {
      const locId = parent.locationId ?? null;
      if (locId != null && String(locId).trim() !== '') {
        return String(locId);
      }
    }
    current = parent;
  }
  return null;
}
